import { useState, useEffect, useRef } from "react";
import { X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface BannerData {
  id: string;
  title: string;
  message: string;
  cta_text: string;
  cta_url: string;
  discount_type?: string;
  discount_value?: number;
  discount_code?: string;
  background_gradient: string;
  text_color: string;
  show_countdown: boolean;
  countdown_duration: number;
  ends_at?: string;
}

interface LimitedOfferBannerProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

const LimitedOfferBanner = ({ onVisibilityChange }: LimitedOfferBannerProps) => {
  const [bannerData, setBannerData] = useState<BannerData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const expiredBannerIds = useRef(new Set<string>());
  const trackedImpressions = useRef(new Set<string>());
  const navigate = useNavigate();

  const getSessionId = () => {
    const storageKey = "solr_banner_session_id";
    const existingSessionId = localStorage.getItem(storageKey);
    if (existingSessionId) return existingSessionId;

    const newSessionId = crypto.randomUUID();
    localStorage.setItem(storageKey, newSessionId);
    return newSessionId;
  };

  const hideBanner = (bannerId?: string) => {
    if (bannerId) {
      expiredBannerIds.current.add(bannerId);
      localStorage.setItem(`banner_expired_${bannerId}`, "true");
    }

    setIsVisible(false);
    setBannerData(null);
    setTimeLeft(0);
    onVisibilityChange?.(false);
  };

  useEffect(() => {
    fetchActiveBanner();
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            hideBanner(bannerData?.id);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeLeft, bannerData?.id, onVisibilityChange]);

  const fetchActiveBanner = async () => {
    try {
      const { data, error } = await supabase
        .from('promotional_banners')
        .select('*')
        .eq('active', true)
        .lte('starts_at', new Date().toISOString())
        .or(`ends_at.is.null,ends_at.gt.${new Date().toISOString()}`)
        .order('priority', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching banner:', error);
        return;
      }
      
      if (data) {
        const hasEnded = data.show_countdown && data.ends_at && new Date(data.ends_at).getTime() <= Date.now();
        const expired = expiredBannerIds.current.has(data.id) || localStorage.getItem(`banner_expired_${data.id}`);

        if (hasEnded || expired) {
          hideBanner(data.id);
          return;
        }

        // Check if user has dismissed this banner
        const dismissed = localStorage.getItem(`banner_dismissed_${data.id}`);
        if (dismissed) {
          hideBanner();
          return;
        }

        const nextTimeLeft = calculateTimeLeft(data);
        if (data.show_countdown && nextTimeLeft <= 0) {
          hideBanner(data.id);
          return;
        }

        setBannerData(data);
        setIsVisible(true);
        onVisibilityChange?.(true);
        setTimeLeft(nextTimeLeft);
        trackImpression(data.id);
      } else {
        hideBanner();
      }
    } catch (error) {
      console.error('Error in fetchActiveBanner:', error);
    }
  };

  const calculateTimeLeft = (banner: BannerData) => {
    if (banner.show_countdown && banner.ends_at) {
      const diff = Math.floor((new Date(banner.ends_at).getTime() - Date.now()) / 1000);
      return Math.max(0, diff);
    }

    if (banner.show_countdown) return Math.max(0, banner.countdown_duration);
    return 0;
  };

  const trackImpression = async (bannerId: string) => {
    if (trackedImpressions.current.has(bannerId)) return;
    trackedImpressions.current.add(bannerId);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.rpc('track_banner_interaction', {
        p_banner_id: bannerId,
        p_interaction_type: 'impression',
        p_session_id: getSessionId(),
        p_user_id: user?.id
      });
    } catch (error) {
      console.error('Error tracking impression:', error);
      trackedImpressions.current.delete(bannerId);
    }
  };

  const handleClick = async () => {
    if (bannerData) {
      const destination = bannerData.cta_url || "/shop";

      if (destination.startsWith("http")) {
        window.location.href = destination;
      } else {
        navigate(destination.startsWith("/") ? destination : `/${destination}`);
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        await supabase.rpc('track_banner_interaction', {
          p_banner_id: bannerData.id,
          p_interaction_type: 'click',
          p_session_id: getSessionId(),
          p_user_id: user?.id
        });
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
  };

  const handleDismiss = async () => {
    if (bannerData) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        await supabase.rpc('track_banner_interaction', {
          p_banner_id: bannerData.id,
          p_interaction_type: 'dismiss',
          p_session_id: getSessionId(),
          p_user_id: user?.id
        });
        
        // Store dismissal in localStorage to prevent re-showing
        localStorage.setItem(`banner_dismissed_${bannerData.id}`, 'true');
      } catch (error) {
        console.error('Error tracking dismissal:', error);
      }
    }
    
    hideBanner();
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isVisible || !bannerData || (bannerData.show_countdown && timeLeft <= 0)) return null;

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-r ${bannerData.background_gradient} ${bannerData.text_color} py-3 px-4 transition-all duration-300`}
      style={{ height: '60px' }}
    >
      <div className="container mx-auto flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-1">
          {bannerData.show_countdown && <Clock className="h-5 w-5 animate-pulse" />}
          <div>
            <p className="font-bold">{bannerData.title}</p>
            <p className="text-sm opacity-90">
              {bannerData.message}
              {bannerData.show_countdown && timeLeft > 0 && ` - Ends in ${formatTime(timeLeft)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleClick}
          >
            {bannerData.cta_text}
          </Button>
          <button
            onClick={handleDismiss}
            className="hover:opacity-75 transition-opacity min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close banner"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LimitedOfferBanner;
